<script setup lang="ts">
import type { URLReference, URL } from '@/models/api-generated'
import IconButton from '../shared/IconButton.vue'
import Modal from '../shared/Modal.vue'
import DocumentFolders from '../document/DocumentFolders.vue'
import { computed, ref, watch } from 'vue'
import { hasPermission, truncate } from '@/services/utils'
import { PermDocumentAdd, PermDocumentRead } from '@/models/enums-generated'
import { toAbsoluteUrl } from '@/services/utils'

const model = defineModel<URLReference[]>({ required: true })

const props = defineProps<{
	canEdit?: boolean
	ownerId?: number
	ownerType: string
}>()

const canAddUrl = computed(() => hasPermission(PermDocumentAdd))
const canView = computed(() => hasPermission(PermDocumentRead))

const showFolderModal = ref(false)
const currentRefIndex = ref<number>()
const pendingSelection = ref<URL>()
const selectedUrl = ref<URL>()

const addRef = () => {
	model.value.push({
		id: 0,
		title: '',
		ownerId: props.ownerId ?? 0,
		ownerType: props.ownerType,
		urlId: 0,
		url: {
			id: 0,
			url: '',
			path: '/',
			referenceCount: 0,
			author: sessionMap.user,
			instance: undefined as any,
		} as URL,
	} as URLReference)
}

const removeRef = (index: number) => model.value.splice(index, 1)

const canEditRef = (refOwnerType: string) => props.canEdit && refOwnerType === props.ownerType

const openUrlModal = (index?: number, existing?: URL) => {
	currentRefIndex.value = index
	showFolderModal.value = true
	pendingSelection.value = existing
}

const closeUrlModal = () => {
	showFolderModal.value = false
	currentRefIndex.value = undefined
	pendingSelection.value = undefined
}

const confirmUrlSelection = () => {
	if (pendingSelection.value) selectedUrl.value = pendingSelection.value
}

watch(selectedUrl, (url) => {
	if (!url || currentRefIndex.value === undefined) return
	const target = model.value[currentRefIndex.value] as any
	target.urlId = url.id
	target.url = url
	if (!target.title && url.name) target.title = url.name
	model.value[currentRefIndex.value].urlId = url.id
	model.value[currentRefIndex.value].url = url

	selectedUrl.value = undefined
	pendingSelection.value = undefined
	currentRefIndex.value = undefined
	showFolderModal.value = false
})

const urlEntity = (ref: URLReference): URL | undefined => {
	const value = (ref as any).url
	return typeof value === 'object' && value ? (value as URL) : undefined
}

const urlValue = (ref: URLReference): string => {
	const entity = urlEntity(ref)
	if (entity?.url) return entity.url
	const raw = (ref as any).url
	return typeof raw === 'string' ? raw : ''
}
</script>

<template>
	<div class="mb-3">
		<div class="d-flex align-items-center mb-2">
			<strong>{{ $t('reference.url') }}</strong>
		</div>
		<template v-if="!model?.length">
			<div>-</div>
		</template>
		<template v-else>
			<div v-for="(ref, index) in model" :key="index" class="mb-2">
				<div class="d-flex flex-column gap-1">
					<template v-if="canEditRef(ref.ownerType)">
						<div class="d-flex gap-2 align-items-start w-100">
							<input
								:data-test="ownerType + '-url-select-' + ownerId"
								type="text"
								class="form-control"
								:placeholder="$t('reference.selectUrl')"
								:value="ref.url.url"
								readonly
								@click="openUrlModal(index, urlEntity(ref))"
							/>
							<IconButton
								:data-test="ownerType + '-delete-reference-' + ownerId"
								class="bi bi-trash"
								:title="$t('misc.delete')"
								@click="removeRef(index)"
								size="fs-5"
							/>
						</div>
						<input
							:data-test="ownerType + '-title-input-' + ownerId"
							type="text"
							class="form-control"
							:placeholder="$t('reference.title')"
							v-model="ref.title"
						/>
					</template>
					<template v-else>
						<div class="d-flex align-items-start gap-2">
							<i class="bi bi-link-45deg text-secondary"></i>
							<a
								v-if="urlValue(ref)"
								:href="toAbsoluteUrl(urlValue(ref))"
								target="_blank"
								class="text-truncate"
								v-one-tooltip="ref.url.url"
							>
								{{ ref.title || truncate(urlValue(ref)) }}
							</a>
							<span v-else>{{ ref.title || '-' }}</span>
						</div>
					</template>
				</div>
				<hr v-if="index !== model.length - 1" class="my-2 border-top border-secondary opacity-25" />
			</div>
		</template>
		<button
			v-if="canAddUrl && props.canEdit && canView"
			:data-test="ownerType + '-add-url-reference-' + ownerId"
			type="button"
			class="btn btn-outline-primary btn-sm mt-1"
			@click="addRef()"
		>
			<i class="bi bi-plus-circle me-1"></i> {{ $t('reference.add') }}
		</button>

		<Modal v-model="showFolderModal" class="modal-xl">
			<template #header>
				<div class="row">
					<div class="ellipsis col-11">{{ $t('document.label') }}</div>
				</div>
			</template>
			<template #body>
				<DocumentFolders
					:show-select="currentRefIndex !== undefined"
					selection-type="url"
					@url-selected="pendingSelection = $event"
					:preselect-url="pendingSelection"
				/>
			</template>
			<template #footer>
				<button class="btn btn-secondary" @click="closeUrlModal">{{ $t('misc.cancel') }}</button>
				<button class="btn btn-primary" :disabled="!pendingSelection" @click="confirmUrlSelection">
					{{ $t('misc.confirm') }}
				</button>
			</template>
		</Modal>
	</div>
</template>

<style scoped>
:deep(.modal-dialog.modal-xl) {
	max-width: 75vw;
}
</style>
